import { ExecutionType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/ExecutionTypeProto';
import Divider from '@/components/common/Divider';
import ExportCSV from '@/components/common/ExportCSV';
import Loader from '@/components/common/Loader';
import PopOut, { PopOutHeader, PopOutTitle } from '@/components/common/PopOut';
import Tooltip from '@/components/common/Tooltip';
import HistoricBlotterTable from '@/components/modules/Blotters/HistoricBlotterTable';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import { selectHistoricTrades, setHistoricTrades } from '@/state/reducers/blotterSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { copyStyles } from '@/utils/popout';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { createPortal } from 'react-dom';
import { MdOpenInNew } from 'react-icons/md';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from '../Modal';
import SearchControls from './SearchControls';

const HistoricSearchModal = (props: ModalProps) => {
    const { opened, handlers } = props;
    const dispatch = useAppDispatch();

    const lang = useLanguage();
    const screen = useScreenSizes();

    const [popOut, setPopOut] = useState<Window | null>(null);

    useEffect(() => {
        dispatch(setHistoricTrades(null));
        if (opened && popOut) {
            popOut.close();
            setPopOut(null);
        }
    }, [opened]);

    const openPopOutWindow = () => {
        const popOutWindow = window.open('', '', 'width=1200,height=600');
        if (popOutWindow) {
            copyStyles(document, popOutWindow.document);
            setPopOut(popOutWindow);
            handlers.close();
        }
    };

    return (
        <Fragment>
            <Modal {...props} size="sm:max-w-[90vw] h-full lg:h-[600px] text-brand-module-title">
                <ModalHeader>
                    <div className="flex space-x-2 items-center">
                        <ModalTitle>{lang.common.historicSearch}</ModalTitle>
                        {(screen.laptop || screen.desktop) && !isTablet && (
                            <Tooltip
                                width={120}
                                content="Open in Separate Window"
                                containerClassName="h-5"
                                placement="top-start">
                                <button type="button" className="text-xl" onClick={openPopOutWindow}>
                                    <MdOpenInNew />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                    <ModalClose handlers={handlers} />
                </ModalHeader>
                <HistoricSearchBody />
            </Modal>
            {popOut &&
                createPortal(
                    <PopOut>
                        <PopOutHeader>
                            <PopOutTitle>{lang.common.historicSearch}</PopOutTitle>
                        </PopOutHeader>
                        <HistoricSearchBody />
                    </PopOut>,
                    popOut.document.body
                )}
        </Fragment>
    );
};

export default HistoricSearchModal;

const HistoricSearchBody = () => {
    const historicTrades = useAppSelector(selectHistoricTrades);

    const lang = useLanguage();
    const [CSVData, setCSVData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [executionType, setExecutiontype] = useState<any>({
        value: 'All',
        label: 'ALL'
    });

    const filteredTrades = useMemo(() => {
        return historicTrades
            ? historicTrades?.filter(
                  (trade) => executionType.value === 'All' || trade.executionType === ExecutionType[executionType.value]
              )
            : [];
    }, [historicTrades]);

    return (
        <div className="flex flex-col w-full h-full">
            <Divider />
            <SearchControls
                isLoading={isLoading}
                executionType={executionType}
                setIsLoading={setIsLoading}
                setExecutiontype={setExecutiontype}>
                {isLoading && (
                    <div className="flex justify-center items-center flex-1">
                        <Loader className="text-brand-primary" />
                    </div>
                )}

                {!isLoading && !!filteredTrades?.length && (
                    <div className="flex flex-col flex-1 p-2">
                        <div className="flex items-center gap-2 mb-2 text-brand-text">
                            <div className="text-sm font-bold">{lang.blotters.searchResults}</div>
                            <ExportCSV csvData={CSVData} />
                        </div>
                        <div className="flex-1 basis-0 overflow-hidden">
                            <HistoricBlotterTable setCSVData={setCSVData} historicTrades={filteredTrades || []} />
                        </div>
                        <div className="text-sm font-bold text-brand-text mt-2">
                            {lang.blotters.rowsReturned}: {CSVData.length}, {lang.blotters.outOfAMaximum} 200
                        </div>
                    </div>
                )}

                {!isLoading && (
                    <>
                        {!historicTrades && (
                            <div className="historic-search-empty p-2 flex justify-center items-center text-center flex-1 text-brand-neutral-400 text-sm font-bold italic">
                                <div>{lang.blotters.fillInAboveCriteria}</div>
                            </div>
                        )}
                        {historicTrades?.length === 0 && (
                            <div className="historic-search-empty p-2 flex justify-center items-center text-center flex-1 text-brand-neutral-400 text-sm font-bold italic">
                                <div>{lang.blotters.noOrdersFound}</div>
                            </div>
                        )}
                    </>
                )}
            </SearchControls>
        </div>
    );
};
